/**
 * PATH TO GO — per-tab status dots (the tab strip's journey mini-map).
 *
 * Pure, cheap predicates only — every dot must trace to derived site state
 * (cockpit contract rule 3: status is derived, never asserted). This is a
 * MINI-MAP, not the decision authority: the orchestrator's stage model stays
 * the single source of truth on the Path to GO hub; these dots answer only
 * "has this tab's surface been worked?" at a glance.
 *
 * Semantics by group:
 *  - REQUIRED-path tabs (Define + Decide groups) show 'open' when unworked —
 *    the journey nudges them.
 *  - OPTIONAL tabs (Analyze group — the side-quests) show 'cleared' when done
 *    but stay NEUTRAL (no dot) when untouched: optional work never nags.
 *    Exception: a STARTED-but-unfinished generation chain shows 'open'
 *    (finish what you started).
 */

export type SiteTabStatus = 'cleared' | 'open' | 'neutral';

/** Coverage statuses that count as a completed intent (mirrors the
 *  orchestrator's COMPLETE_STATUSES for the site-project-intent row). */
const INTENT_COMPLETE_STATUSES = new Set([
  'authored-strong',
  'strong',
  'authored',
  'adequate',
]);

export interface TabStatusInputs {
  /** `site.coverage?.['site-project-intent']?.status ?? null` */
  intentCoverageStatus: string | null;
  /** `siteHasBoundary(site)` */
  hasBoundary: boolean;
  documentCount: number;
  sedPresent: boolean;
  /** `isStaleSed(site)` — cheap field check. */
  sedStale: boolean;
  /** Distinct current analysis types, including semantic-only revisions. */
  analysisCount: number;
  /** A decision basis exists and has cleared the adequacy gate. */
  decisionReady: boolean;
  compositePresent: boolean;
  /**
   * null = never audited (neutral dot).
   * false = the last audit found blocking findings, OR it is STALE — reports
   *         changed since it ran, so its verdict no longer describes the site.
   * true  = a CURRENT audit with no blocking findings.
   *
   * Staleness has to fold into this. The result is persisted in Firestore and
   * survives sessions, so without a currency check a months-old "clean" run
   * would keep showing a green tick over reports it never saw.
   */
  auditClean: boolean | null;
  hasSimulation: boolean;
  financialAssumptionsSet: boolean;
  supplyChainReady: boolean;
  /** null = the G&I workflow was never entered (neutral). */
  generationReady: boolean | null;
  roadmap: { completed: number; total: number };
}

export function tabStatus(tab: string, s: TabStatusInputs): SiteTabStatus {
  switch (tab) {
    // Define — required path.
    case 'intent':
      return s.intentCoverageStatus != null && INTENT_COMPLETE_STATUSES.has(s.intentCoverageStatus)
        ? 'cleared'
        : 'open';
    case 'map':
      return s.hasBoundary ? 'cleared' : 'open';
    case 'documents':
      return s.documentCount > 0 ? 'cleared' : 'open';
    case 'evidence':
      return s.sedPresent && !s.sedStale ? 'cleared' : 'open';

    // Decide — required path.
    case 'reports':
      return s.analysisCount > 0 ? 'cleared' : 'open';
    case 'decision':
      return s.decisionReady ? 'cleared' : 'open';
    case 'stakeholder':
      return s.analysisCount > 0 ? 'cleared' : 'open';
    case 'audit':
      if (s.auditClean == null) return 'neutral';
      return s.auditClean ? 'cleared' : 'open';
    case 'deepdive':
      return s.compositePresent ? 'cleared' : 'neutral';
    case 'roadmap':
      if (s.roadmap.total === 0) return 'neutral';
      return s.roadmap.completed >= s.roadmap.total ? 'cleared' : 'open';

    // Analyze — optional: cleared when done, neutral when untouched.
    case 'simulations':
      return s.hasSimulation ? 'cleared' : 'neutral';
    case 'financial':
      return s.financialAssumptionsSet ? 'cleared' : 'neutral';
    case 'supplychain':
      return s.supplyChainReady ? 'cleared' : 'neutral';
    case 'generation':
      if (s.generationReady == null) return 'neutral';
      return s.generationReady ? 'cleared' : 'open';

    // home / pathtogo / anything else — the hub carries its own meter.
    default:
      return 'neutral';
  }
}
